import { useEffect, useState } from "react";
import { isFinished, SYSTEM_EXCLUSIONS, type MediaAgent, type MediaRun, type MediaRunKind, type MediaSource } from "../../../shared/media";
import { useMediaActions, useMediaOverview, useMediaRun } from "../../data/mediaQueries";
import { Icon } from "../../ui/Icon";
import { formatBytes, formatDuration, MediaReportView } from "./MediaReport";
import { MediaReview } from "./MediaReview";

const KIND_LABEL: Record<MediaRunKind, string> = {
  inventory: "Library scan",
  benchmark: "Speed test (500 files)",
  analyze_sample: "Analysis: sample (500)",
  analyze: "Analysis: whole library",
  update: "Process new photos",
};

const STATUS_LABEL: Record<MediaRun["status"], string> = {
  queued: "Waiting to start",
  running: "Running",
  paused: "Paused",
  completed: "Finished",
  failed: "Failed",
  cancelled: "Cancelled",
};

const when = (iso: string | null) => (iso ? new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—");

/**
 * Photo library analysis. The PC agent does the work against the home
 * network share; this page starts runs, shows progress and reads reports.
 */
export function MediaSection() {
  const overview = useMediaOverview();
  const [selected, setSelected] = useState<string | null>(null);
  const data = overview.data;
  const source = data?.sources[0] ?? null;
  const runs = data?.runs ?? [];
  const active = runs.find((r) => !isFinished(r.status)) ?? null;
  const shownId = selected ?? runs.find((r) => r.hasReport)?.id ?? null;
  const shown = useMediaRun(shownId, !!shownId && shownId === active?.id);
  const reviewer = data?.agents.find((a) => a.info?.review) ?? null;

  return (
    <div className="form media">
      <h3>Photo library</h3>
      <p className="muted">
        Your PC reads the photo share, works out dates, details and formats, and reports back here. Nothing on the share is changed, and the photos and file list stay on
        your PC.
      </p>
      {overview.isError && <p className="error-text">{(overview.error as Error).message}</p>}
      {reviewer && (
        // On a phone, Review sits far down the page: a clear way straight there
        // (a button, not a #link: the admin page's sections live in the address's #.)
        <button className="btn btn-primary media-review-jump" onClick={() => document.getElementById("media-review")?.scrollIntoView({ behavior: "smooth" })}>
          <Icon name="image" size="1.1rem" /> Review photos
        </button>
      )}

      <AgentPanel agents={data?.agents ?? []} />
      <hr />
      <SourcePanel source={source} />
      <hr />
      <RunsPanel source={source} runs={runs} active={active} agentOnline={!!data?.agents.some((a) => a.online)} selected={shownId} onSelect={setSelected} />
      {shown.data?.report && (
        <>
          <hr />
          <MediaReportView run={shown.data} report={shown.data.report} />
        </>
      )}
      {reviewer && (
        <>
          <hr />
          <MediaReview agent={reviewer} />
        </>
      )}
    </div>
  );
}

function AgentPanel({ agents }: { agents: MediaAgent[] }) {
  const { pairing, disconnect } = useMediaActions();
  const [code, setCode] = useState<{ code: string; expiresAt: string } | null>(null);
  const makeCode = () => pairing.mutate(undefined, { onSuccess: setCode });
  // A fresh pairing code is no longer needed once the new PC shows up.
  const count = agents.length;
  const [countAtCode, setCountAtCode] = useState(count);
  useEffect(() => {
    if (code && count > countAtCode) setCode(null);
  }, [code, count, countAtCode]);

  return (
    <section>
      <h4>Your PC</h4>
      {agents.length === 0 && <p className="muted small">No PC is connected yet. The Kinmantle agent runs quietly on your PC and does the heavy lifting.</p>}
      <ul className="media-agents">
        {agents.map((a) => (
          <li key={a.id}>
            <span className={`dot ${a.online ? "on" : ""}`} aria-hidden />
            <div>
              <strong>{a.name}</strong>
              <span className="muted small">
                {a.online ? "Online" : `Offline · last seen ${when(a.lastSeenAt)}`}
                {a.info?.gpu ? ` · ${a.info.gpu}` : ""}
                {a.info?.freeDiskGb != null ? ` · ${Math.round(a.info.freeDiskGb)} GB free` : ""}
              </span>
              {a.info && Object.keys(a.info.tools).length > 0 && (
                <span className="muted small">
                  {Object.entries(a.info.tools)
                    .map(([k, v]) => `${k} ${v}`)
                    .join(" · ")}
                </span>
              )}
            </div>
            <button className="btn btn-sm btn-ghost" onClick={() => confirm(`Disconnect ${a.name}? It will stop picking up work until it's connected again.`) && disconnect.mutate(a.id)}>
              Disconnect
            </button>
          </li>
        ))}
      </ul>
      {code ? (
        <div className="media-pair">
          <p>
            Connection code: <strong className="media-code">{code.code}</strong> <span className="muted small">(works once, until {when(code.expiresAt)})</span>
          </p>
          <p className="muted small">On the PC, open PowerShell in the Kinmantle folder and run:</p>
          <pre className="media-cmd">
            powershell -ExecutionPolicy Bypass -File agent\install.ps1 -Server {window.location.origin} -Code {code.code}
          </pre>
        </div>
      ) : (
        <button
          className="btn"
          onClick={() => {
            setCountAtCode(count);
            makeCode();
          }}
          disabled={pairing.isPending}
        >
          <Icon name="plus" size="1rem" /> {agents.length ? "Connect another PC" : "Connect a PC"}
        </button>
      )}
      {pairing.isError && <p className="error-text">{pairing.error.message}</p>}
    </section>
  );
}

function SourcePanel({ source }: { source: MediaSource | null }) {
  const { saveSource } = useMediaActions();
  const [path, setPath] = useState(source?.rootPath ?? "");
  const [exclusions, setExclusions] = useState(source?.exclusions.join("\n") ?? "");
  const [msg, setMsg] = useState<string | null>(null);
  // Fill the form once the overview arrives.
  useEffect(() => {
    if (source) {
      setPath(source.rootPath);
      setExclusions(source.exclusions.join("\n"));
    }
  }, [source?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = () =>
    saveSource.mutate(
      { id: source?.id ?? null, input: { name: "", rootPath: path, exclusions: exclusions.split("\n") } },
      { onSuccess: () => setMsg("Saved"), onError: (e) => setMsg(e.message) },
    );
  const dirty = !source || path.trim() !== source.rootPath || exclusions.split("\n").map((s) => s.trim()).filter(Boolean).join("\n") !== source.exclusions.join("\n");

  return (
    <section>
      <h4>Photo folder</h4>
      <label className="field">
        <span>Network share</span>
        <input className="input" value={path} onChange={(e) => setPath(e.target.value)} placeholder="\\nas\photos" spellCheck={false} />
      </label>
      <label className="field">
        <span>Folders to skip (one per line, relative to the share; * works as a wildcard)</span>
        <textarea className="input textarea" rows={3} value={exclusions} onChange={(e) => setExclusions(e.target.value)} placeholder="Private" spellCheck={false} />
      </label>
      <p className="muted small">Always skipped: {SYSTEM_EXCLUSIONS.join(", ")}.</p>
      <div className="row">
        <button className="btn btn-primary" onClick={save} disabled={!dirty || !path.trim() || saveSource.isPending}>
          Save folder
        </button>
        {msg && <span className={msg === "Saved" ? "muted" : "error-text"}>{msg}</span>}
      </div>
    </section>
  );
}

function RunsPanel(props: { source: MediaSource | null; runs: MediaRun[]; active: MediaRun | null; agentOnline: boolean; selected: string | null; onSelect: (id: string) => void }) {
  const { source, runs, active, agentOnline, selected, onSelect } = props;
  const { start } = useMediaActions();
  const done = (kind: MediaRunKind) => runs.some((r) => r.kind === kind && r.status === "completed");
  const go = (kind: MediaRunKind) => source && start.mutate({ sourceId: source.id, kind });
  const goAll = () =>
    confirm("Analyse the whole library? It runs on your PC for a few days (overnight is fine), can be paused any time, and changes nothing on the share.") && go("analyze");

  return (
    <section>
      <h4>Analysis</h4>
      {!active && (
        <>
          {done("analyze") && (
            <div className="media-update">
              <button className="btn btn-primary" onClick={() => go("update")} disabled={!source || start.isPending}>
                <Icon name="sparkle" size="1rem" /> Process new photos
              </button>
              <p className="muted small">
                Finds photos added or changed since the last scan and runs everything on them only: tags, faces, privacy checks, look-alikes and descriptions. Then the
                wall's copy is brought up to date. Usually 10 to 30 minutes, most of it the library scan.
              </p>
            </div>
          )}
          <div className="row">
            <button className="btn" onClick={() => go("inventory")} disabled={!source || start.isPending}>
              <Icon name="refresh" size="1rem" /> {done("inventory") ? "Scan library again" : "Scan library"}
            </button>
            <button className="btn" onClick={() => go("benchmark")} disabled={!source || start.isPending}>
              <Icon name="gauge" size="1rem" /> Speed test (500 files)
            </button>
          </div>
          <div className="row">
            <button className="btn btn-primary" onClick={() => go("analyze_sample")} disabled={!source || !done("inventory") || start.isPending}>
              <Icon name="sparkle" size="1rem" /> Analyze sample (500)
            </button>
            <button className="btn" onClick={goAll} disabled={!source || !done("analyze_sample") || start.isPending}>
              <Icon name="play" size="1rem" /> Analyze whole library
            </button>
          </div>
        </>
      )}
      {!source && <p className="muted small">Save a photo folder first.</p>}
      {source && !active && (
        <p className="muted small">
          The scan lists every file and reads its dates and camera details. Analyze reads each photo once, then your PC's AI tags it, finds faces and look-alikes, flags
          anything sensitive for you to check, and writes a short description. Try the 500-file sample first and review it below; the whole library unlocks after that.
          All of it is read-only.
        </p>
      )}
      {start.isError && <p className="error-text">{start.error.message}</p>}
      {active && <ActiveRun run={active} agentOnline={agentOnline} />}

      {runs.length > 0 && (
        <table className="table media-runs">
          <thead>
            <tr>
              <th>Run</th>
              <th>Started</th>
              <th>Status</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {runs.map((r) => (
              <tr key={r.id} className={r.id === selected ? "on" : ""}>
                <td>{KIND_LABEL[r.kind]}</td>
                <td>{when(r.startedAt ?? r.createdAt)}</td>
                <td>
                  {STATUS_LABEL[r.status]}
                  {r.status === "failed" && r.error ? <span className="muted small"> · {r.error}</span> : null}
                </td>
                <td>
                  {r.hasReport && (
                    <button className="btn btn-sm btn-ghost" onClick={() => onSelect(r.id)} disabled={r.id === selected}>
                      {r.id === selected ? "Showing" : "Report"}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

function ActiveRun({ run, agentOnline }: { run: MediaRun; agentOnline: boolean }) {
  const { control } = useMediaActions();
  const p = run.progress;
  const pct = p?.total ? Math.min(100, (p.done / p.total) * 100) : null;
  const act = (action: "pause" | "resume" | "cancel") => control.mutate({ id: run.id, action });
  const paused = run.control === "pause";
  const cancelling = run.control === "cancel";

  return (
    <div className="media-active">
      <div className="media-active-head">
        <strong>{KIND_LABEL[run.kind]}</strong>
        <span className="muted small">
          {cancelling ? "Stopping…" : paused ? (run.status === "paused" ? "Paused" : "Pausing…") : run.waitingForAgent ? "Waiting for your PC" : STATUS_LABEL[run.status]}
        </span>
      </div>
      {run.waitingForAgent && (
        <p className="muted small">
          {agentOnline ? "Your PC will pick this up in a moment." : "The agent on your PC is off or asleep. The run is saved and continues where it left off once the PC is back."}
        </p>
      )}
      {p && (
        <>
          <div className="media-stage">{p.stageLabel}</div>
          <div className={`media-bar ${pct == null ? "indeterminate" : ""}`} role="progressbar" aria-valuenow={pct ?? undefined} aria-valuemin={0} aria-valuemax={100}>
            <span style={{ width: pct == null ? undefined : `${pct}%` }} />
          </div>
          <div className="muted small media-stats">
            <span>
              {p.done.toLocaleString()}
              {p.total != null ? ` of ${p.total.toLocaleString()}` : ""} files
            </span>
            {p.bytesDone != null && <span>{formatBytes(p.bytesDone)}{p.bytesTotal ? ` of ${formatBytes(p.bytesTotal)}` : ""}</span>}
            {!paused && p.filesPerSec != null && <span>{p.filesPerSec.toFixed(p.filesPerSec < 10 ? 1 : 0)} files/s</span>}
            {!paused && p.mbPerSec != null && <span>{p.mbPerSec.toFixed(1)} MB/s</span>}
            {!paused && p.etaSeconds != null && <span>about {formatDuration(p.etaSeconds)} left</span>}
            {!!p.failed && <span className="warn-text">{p.failed} problems</span>}
          </div>
          {p.message && <p className="muted small">{p.message}</p>}
        </>
      )}
      <div className="row">
        {paused ? (
          <button className="btn" onClick={() => act("resume")} disabled={control.isPending || cancelling}>
            <Icon name="play" size="1rem" /> Resume
          </button>
        ) : (
          <button className="btn" onClick={() => act("pause")} disabled={control.isPending || cancelling}>
            <Icon name="pause" size="1rem" /> Pause
          </button>
        )}
        <button className="btn btn-danger-ghost" onClick={() => confirm("Cancel this run? Work already done is kept on your PC.") && act("cancel")} disabled={control.isPending || cancelling}>
          Cancel
        </button>
      </div>
      {control.isError && <p className="error-text">{control.error.message}</p>}
    </div>
  );
}
